import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Business, Search } from '@mui/icons-material';
import { Button, TextField, Card, CardContent } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetV2AdminOrganizationsQuery } from '../store/hooks';

export default function OrganizationsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || undefined;

  const { data, isLoading, error } = useGetV2AdminOrganizationsQuery({
    page,
    perPage: 20,
    search,
  });

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (searchInput.trim()) {
      params.set('search', searchInput.trim());
    }
    params.set('page', '1');
    setSearchParams(params);
  }, [searchInput, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleRowClick = useCallback((org: any) => {
    navigate(`/admin-v2/organization/${org.organization_id}`);
  }, [navigate]);

  const columns = useMemo<any[]>(() => [
    {
      key: 'name',
      label: 'Organization Name',
      render: (value: unknown) => (
        <div className="flex items-center">
          <Business className="h-5 w-5 text-gray-400 mr-2" />
          <span className="font-medium text-gray-900">{value as string}</span>
        </div>
      ),
    },
    {
      key: 'member_count',
      label: 'Members',
      render: (value: unknown) => (
        <span className="text-gray-600">{String(value || 0)}</span>
      ),
    },
    {
      key: 'active_member_count',
      label: 'Active Members',
      render: (value: unknown) => (
        <span className="text-gray-600">{String(value || 0)}</span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: unknown) => {
        if (!value) return <span className="text-gray-400">-</span>;
        const date = new Date(value as string);
        return (
          <div className="text-sm">
            <div className="text-gray-900">{formatDateTime(date)}</div>
            <div className="text-gray-500">{formatRelativeTime(date)}</div>
          </div>
        );
      },
    },
    {
      key: 'organization_id',
      label: 'Actions',
      render: (_: unknown, org: any) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin-v2/organization/${org.organization_id}`);
          }}
        >
          View Details
        </Button>
      ),
    },
  ], [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading organizations</h2>
            <p className="text-gray-600 mt-2">Unable to fetch organizations list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Organizations</h1>

          <Card className="mb-6">
            <CardContent>
              <div className="flex gap-2">
                <TextField
                  fullWidth
                  placeholder="Search organizations by name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  InputProps={{
                    startAdornment: <Search className="mr-2 h-5 w-5 text-gray-400" />,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          {isLoading && !data ? (
            <LoadingState message="Loading organizations..." />
          ) : (
            <>
              <DataTable
                title="Organizations"
                columns={columns}
                data={data?.items || []}
                total={data?.total}
                loading={isLoading}
                onRowClick={handleRowClick}
                emptyMessage="No organizations found"
              />

              {data && data.total_pages && data.total_pages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                  <Button
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="py-2 px-4">
                    Page {page} of {data.total_pages}
                  </span>
                  <Button
                    disabled={!data.total_pages || page >= data.total_pages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}